import { useState, useEffect } from "react";
import { ArrowLeft, MessageSquare, X } from "lucide-react";
import { proApi } from "../../lib/api";
import { notify } from "../../lib/notify";
import { useIsMobile } from "../../hooks/useIsMobile";

function FeedbackForm({
	onClose,
	fullScreen,
}: { onClose: () => void; fullScreen?: boolean }) {
	const [category, setCategory] = useState("general");
	const [message, setMessage] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!message.trim()) return;

		setIsSubmitting(true);
		try {
			await proApi.submitFeedback({
				category,
				message: message.trim(),
				pageUrl: window.location.href,
				browser: navigator.userAgent,
				screenResolution: `${window.screen.width}x${window.screen.height}`,
				location: Intl.DateTimeFormat().resolvedOptions().timeZone,
			});
			notify.success("Thank you for your feedback!");
			setMessage("");
			setCategory("general");
			onClose();
		} catch {
			notify.error("Failed to submit feedback. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	if (fullScreen) {
		return (
			<div className="fixed inset-0 z-[60] flex flex-col bg-background-base animate-page-in">
				<div className="flex h-14 items-center gap-3 border-b border-border-default bg-background-elevated px-4 flex-shrink-0">
					<button
						type="button"
						onClick={onClose}
						className="p-2 -ml-2 text-foreground-muted hover:text-foreground-default rounded-md"
						aria-label="Go back"
					>
						<ArrowLeft className="h-5 w-5" />
					</button>
					<h2 className="text-lg font-semibold text-foreground-default">
						Send Feedback
					</h2>
				</div>
				<form onSubmit={handleSubmit} className="flex-1 flex flex-col p-4">
					<select
						value={category}
						onChange={(e) => setCategory(e.target.value)}
						className="w-full px-3 py-2 border border-border-default rounded-md bg-background-base text-foreground-default text-sm mb-3"
					>
						<option value="general">General Feedback</option>
						<option value="bug">Bug Report</option>
						<option value="feature_request">Feature Request</option>
					</select>
					<textarea
						value={message}
						onChange={(e) => setMessage(e.target.value)}
						placeholder="What's on your mind?"
						className="w-full flex-1 px-3 py-2 border border-border-default rounded-md bg-background-base text-foreground-default text-sm resize-none min-h-32"
					/>
					<button
						type="submit"
						disabled={isSubmitting || !message.trim()}
						className="w-full mt-4 px-4 py-3 bg-primary-500 text-white rounded-md font-semibold text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{isSubmitting ? "Submitting..." : "Submit Feedback"}
					</button>
				</form>
			</div>
		);
	}

	return (
		<div className="absolute bottom-full right-0 mb-3 w-80 bg-background-elevated border border-border-default rounded-xl p-5 shadow-xl">
			<div className="flex items-center justify-between mb-3">
				<h3 className="text-base font-bold text-foreground-default">
					Send Feedback
				</h3>
				<button
					type="button"
					onClick={onClose}
					className="text-foreground-muted hover:text-foreground-default"
					aria-label="Close feedback form"
				>
					<X size={18} />
				</button>
			</div>
			<form onSubmit={handleSubmit}>
				<select
					value={category}
					onChange={(e) => setCategory(e.target.value)}
					className="w-full px-3 py-2 border border-border-default rounded-md bg-background-base text-foreground-default text-sm mb-2.5"
				>
					<option value="general">General Feedback</option>
					<option value="bug">Bug Report</option>
					<option value="feature_request">Feature Request</option>
				</select>
				<textarea
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					placeholder="What's on your mind?"
					className="w-full px-3 py-2 border border-border-default rounded-md bg-background-base text-foreground-default text-sm mb-2.5 resize-y min-h-20"
					rows={3}
				/>
				<button
					type="submit"
					disabled={isSubmitting || !message.trim()}
					className="w-full px-4 py-2 bg-primary-500 text-white rounded-md font-semibold text-sm hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					{isSubmitting ? "Submitting..." : "Submit Feedback"}
				</button>
			</form>
		</div>
	);
}

export function FeedbackButton() {
	const [isFormOpen, setIsFormOpen] = useState(false);
	const isMobile = useIsMobile();

	useEffect(() => {
		const handler = () => setIsFormOpen(true);
		window.addEventListener("open-feedback", handler);
		return () => window.removeEventListener("open-feedback", handler);
	}, []);

	// Mobile: only render when form is open (triggered via More sheet event)
	if (isMobile) {
		if (!isFormOpen) return null;
		return <FeedbackForm onClose={() => setIsFormOpen(false)} fullScreen />;
	}

	// Desktop: compact icon button + popover
	return (
		<div className="fixed bottom-4 right-4 z-40">
			{/* Feedback form popover */}
			{isFormOpen && (
				<FeedbackForm onClose={() => setIsFormOpen(false)} />
			)}

			{/* Compact icon button */}
			<button
				type="button"
				onClick={() => setIsFormOpen(!isFormOpen)}
				aria-label="Send Feedback"
				title="Send Feedback"
				className="flex h-8 w-8 items-center justify-center rounded-full border border-border-default bg-background-elevated text-foreground-subtle shadow-sm transition-colors hover:bg-background-muted hover:text-foreground-muted opacity-60 hover:opacity-100"
			>
				<MessageSquare size={14} />
			</button>
		</div>
	);
}
